import { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  QrCode,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
} from "lucide-react";

const setupSchema = z.object({
  username: z
    .string()
    .min(3, "Username must be at least 3 characters")
    .max(30, "Username must be 30 characters or less")
    .regex(/^[a-z0-9_-]+$/, "Lowercase letters, numbers, _ and - only"),
});

type UsernameStatus = "idle" | "checking" | "available" | "unavailable";

export default function SetupUsername() {
  const [usernameStatus, setUsernameStatus] = useState<UsernameStatus>("idle");
  const [usernameMessage, setUsernameMessage] = useState("");
  const [usernameHint, setUsernameHint] = useState("");
  const [usernameReason, setUsernameReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const form = useForm<z.infer<typeof setupSchema>>({
    resolver: zodResolver(setupSchema),
    defaultValues: { username: "" },
  });

  const usernameValue = form.watch("username");

  // Extract token from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("token");
    if (!t) {
      window.location.href = "/login";
      return;
    }
    localStorage.setItem("commonqr_token", t);
  }, []);

  // Live username availability check — 2 second debounce
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = usernameValue?.trim().toLowerCase() ?? "";
    if (trimmed.length < 3) {
      setUsernameStatus("idle");
      setUsernameMessage("");
      setUsernameHint("");
      setUsernameReason("");
      return;
    }
    setUsernameStatus("checking");
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `/api/v1/check/username/${encodeURIComponent(trimmed)}`,
        );
        const data = await res.json();
        if (data.available) {
          setUsernameStatus("available");
          setUsernameMessage("");
          setUsernameHint("");
          setUsernameReason("");
        } else {
          setUsernameStatus("unavailable");
          setUsernameMessage(data.message || "Username is not available.");
          setUsernameHint(data.hint || "");
          setUsernameReason(data.reason || "");
        }
      } catch {
        setUsernameStatus("idle");
      }
    }, 2000);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [usernameValue]);

  const onSubmit = async (data: z.infer<typeof setupSchema>) => {
    if (usernameStatus === "checking") {
      form.setError("username", {
        message: "Please wait — checking username availability...",
      });
      return;
    }
    if (usernameStatus !== "available") {
      form.setError("username", {
        message: "Please choose an available username before continuing.",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const checkRes = await fetch(
        `/api/v1/check/username/${encodeURIComponent(data.username)}`,
      );
      const checkData = await checkRes.json();
      if (!checkData.available) {
        setUsernameStatus("unavailable");
        setUsernameMessage(checkData.message || "Username is not available.");
        setUsernameHint(checkData.hint || "");
        setUsernameReason(checkData.reason || "");
        form.setError("username", {
          message: "Username was just taken — please choose another.",
        });
        setIsSubmitting(false);
        return;
      }

      const res = await fetch("/api/v1/setup/username", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("commonqr_token")}`,
        },
        body: JSON.stringify({ username: data.username }),
      });

      if (res.ok) {
        window.location.href = "/dashboard";
      } else {
        const err = await res.json();
        form.setError("username", {
          message: err.message || "Failed to set username",
        });
      }
    } catch {
      form.setError("username", {
        message: "Something went wrong. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isDisabled =
    isSubmitting ||
    usernameStatus === "checking" ||
    usernameStatus !== "available";
  const isReserved = usernameReason === "USERNAME_PLATFORM_RESERVED";

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4 py-12">
      <Helmet>
        <title>Choose Your Username — CommonQR</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="w-full max-w-md bg-card rounded-[2rem] p-8 shadow-2xl shadow-black/5 border border-border/50">
        <div className="flex justify-center mb-6">
          <div className="bg-primary/10 p-3 rounded-2xl text-primary">
            <QrCode size={32} strokeWidth={2.5} />
          </div>
        </div>

        <h1 className="text-3xl font-display font-bold text-center mb-2">
          Choose Your Username
        </h1>
        <p className="text-center text-muted-foreground mb-6">
          This will be your permanent QR profile URL.
        </p>

        {/* URL Preview */}
        {usernameValue && usernameStatus === "available" && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 mb-6 text-center">
            <p className="text-xs text-emerald-600 font-medium mb-1">
              Your QR profile URL will be:
            </p>
            <p className="text-emerald-700 font-bold">
              commonqr.com/{usernameValue}
            </p>
          </div>
        )}

        {/* Important notice */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-4 mb-6">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-amber-800 mb-2">
                Please read before choosing:
              </p>
              <ul className="text-xs text-amber-700 space-y-1">
                <li>
                  • This URL is linked to your QR code — it{" "}
                  <strong>never changes</strong>
                </li>
                <li>
                  • Once set, it <strong>cannot be modified</strong> — choose
                  carefully
                </li>
                <li>• Use your real name, business name, or brand name</li>
                <li>• Lowercase letters, numbers, _ and - only</li>
                <li>• 3 to 30 characters</li>
                <li>
                  • Pro plan allows additional profiles with separate URLs
                </li>
              </ul>
            </div>
          </div>
        </div>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              Your Username
            </label>
            <div className="relative flex items-center">
              <span className="absolute left-4 text-muted-foreground text-sm select-none pointer-events-none">
                commonqr.com/
              </span>
              <Input
                placeholder="yourname"
                autoComplete="off"
                {...form.register("username", {
                  onChange: (e) => {
                    e.target.value = e.target.value.toLowerCase();
                  },
                })}
                className={`w-full pl-32 pr-10 py-6 rounded-xl bg-background border-2 focus:ring-primary/10 transition-colors ${
                  usernameStatus === "available"
                    ? "border-emerald-400 focus:border-emerald-500"
                    : usernameStatus === "unavailable"
                      ? isReserved
                        ? "border-amber-400 focus:border-amber-500"
                        : "border-destructive focus:border-destructive"
                      : ""
                }`}
              />
              {usernameStatus === "checking" && (
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground absolute right-4 top-1/2 -translate-y-1/2" />
              )}
              {usernameStatus === "available" && (
                <CheckCircle2 className="w-4 h-4 text-emerald-500 absolute right-4 top-1/2 -translate-y-1/2" />
              )}
              {usernameStatus === "unavailable" && (
                <XCircle
                  className={`w-4 h-4 absolute right-4 top-1/2 -translate-y-1/2 ${isReserved ? "text-amber-500" : "text-destructive"}`}
                />
              )}
            </div>

            {usernameStatus === "checking" && (
              <p className="text-sm text-muted-foreground mt-1.5">
                Checking availability...
              </p>
            )}
            {usernameStatus === "available" && (
              <p className="text-sm text-emerald-600 mt-1.5 flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" /> Username is available!
              </p>
            )}
            {usernameStatus === "unavailable" && usernameMessage && (
              <div className="mt-1.5">
                <p
                  className={`text-sm flex items-center gap-1 ${isReserved ? "text-amber-600" : "text-destructive"}`}
                >
                  <XCircle className="w-3.5 h-3.5 shrink-0" />
                  {usernameMessage}
                </p>
                {usernameHint && (
                  <p className="text-xs text-muted-foreground mt-1 ml-5">
                    {usernameHint}
                  </p>
                )}
              </div>
            )}
            {form.formState.errors.username && (
              <p className="text-sm text-destructive mt-1.5">
                {form.formState.errors.username.message}
              </p>
            )}
          </div>

          <Button
            type="submit"
            disabled={isDisabled}
            className="w-full py-6 rounded-xl font-bold text-lg shadow-lg shadow-primary/25 hover:shadow-xl hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
          >
            {isSubmitting ? (
              <Loader2 className="w-6 h-6 animate-spin" />
            ) : (
              "Confirm & Create My QR Profile"
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
